/**
 * WP-50 — the grid's working copy, its engine context, its change stream and
 * its session undo stack (plan 8.1, plan 5.8).
 *
 * WHY THE MODEL LIVES IN A REF. Every operation has to (1) read the value a
 * cell shows right now, (2) apply the edit, (3) read the value it shows after
 * the engine has recomputed, and (4) emit ONE cta_ws_changes-shaped event
 * carrying both. That is a read-after-write inside a single event handler; a
 * useState value would still be the pre-edit one. So the working copy and the
 * engine context are refs, and a `rev` counter is the only React state — it
 * exists to trigger the re-render. Nothing outside this hook mutates them.
 *
 * The engine context is rebuilt from scratch after every change: it memoises
 * formula results internally, so a stale context would show a stale total.
 * Rebuilding is a Map walk over the workbook's cells — cheap at the size of a
 * sponsor's budget, and the price of a recompute that is always right (G7).
 */
import { useCallback, useRef, useState } from 'react';
import {
  WorkbookContext,
  columnIndexToLetter,
  createWorkbookContext,
  formatCellRef,
  parseCellRef,
} from './engineBridge';
import { cellValueText } from './display';
import {
  StructuralResult,
  WorkingSheet,
  cellAt,
  findSheet,
  headerRowOf,
  insertColumns,
  insertRows,
  parseEntry,
  toEngineSheets,
  toGridSheets,
  toWorking,
  writeCell,
} from './model';
import {
  GridChangeEvent,
  GridSheet,
  InsertColumnEvent,
  InsertRowEvent,
  UnshiftedFormula,
} from './types';

interface ModelState {
  seeded: GridSheet[] | null;
  sheets: WorkingSheet[];
  ctx: WorkbookContext | null;
  error: string | null;
}

interface HistoryEntry {
  /** The working copy as it stood BEFORE the operation (sheets are immutable). */
  sheets: WorkingSheet[];
  /** The changes that undo the operation, already inverted. */
  inverse: PendingChange[];
  /** A structural insertion: the grid's session undo stops here (see README). */
  structural: boolean;
}

interface PendingChange {
  sheet: string;
  cell_ref: string;
  old_value: string | null;
  new_value: string | null;
  old_formula: string | null;
  new_formula: string | null;
  reason: string | null;
}

export interface BudgetGridCallbacks {
  onChange?: (change: GridChangeEvent) => void;
  onInsertColumn?: (event: InsertColumnEvent) => void;
  onInsertRow?: (event: InsertRowEvent) => void;
}

export interface UseBudgetGrid {
  sheets: WorkingSheet[];
  ctx: WorkbookContext | null;
  error: string | null;
  rev: number;
  canUndo: boolean;
  commitEdit: (sheet: string, row: number, col: number, text: string) => void;
  pasteBlock: (sheet: string, row: number, col: number, block: string[][]) => void;
  insertColumn: (sheet: string, atCol: number, count: number, header: string | null) => void;
  insertRow: (sheet: string, atRow: number, count: number) => void;
  undo: () => boolean;
  getSheets: () => GridSheet[];
}

function buildContext(sheets: WorkingSheet[]): { ctx: WorkbookContext | null; error: string | null } {
  try {
    return { ctx: createWorkbookContext(toEngineSheets(sheets), { formulaInputs: 'recompute' }), error: null };
  } catch (e) {
    return { ctx: null, error: e instanceof Error ? e.message : String(e) };
  }
}

function invert(change: PendingChange, reason: string): PendingChange {
  return {
    sheet: change.sheet,
    cell_ref: change.cell_ref,
    old_value: change.new_value,
    new_value: change.old_value,
    old_formula: change.new_formula,
    new_formula: change.old_formula,
    reason: reason,
  };
}

export function useBudgetGrid(
  sheets: GridSheet[],
  readOnly: boolean,
  callbacks: BudgetGridCallbacks,
): UseBudgetGrid {
  const [rev, setRev] = useState(0);
  const stateRef = useRef<ModelState>({ seeded: null, sheets: [], ctx: null, error: null });
  const historyRef = useRef<HistoryEntry[]>([]);
  const seqRef = useRef(0);
  const batchRef = useRef(0);
  const callbacksRef = useRef<BudgetGridCallbacks>(callbacks);
  callbacksRef.current = callbacks;

  // Seed (and re-seed) from the payload. Derived state, so it happens during
  // render rather than in an effect: the first paint must already show the
  // workbook. A NEW `sheets` array identity means a new working copy — the
  // session's undo history belongs to the copy it was recorded against.
  if (stateRef.current.seeded !== sheets) {
    try {
      const working = toWorking(sheets);
      const built = buildContext(working);
      stateRef.current = { seeded: sheets, sheets: working, ctx: built.ctx, error: built.error };
    } catch (e) {
      stateRef.current = {
        seeded: sheets,
        sheets: [],
        ctx: null,
        error: e instanceof Error ? e.message : String(e),
      };
    }
    historyRef.current = [];
  }

  const nextBatchId = useCallback((): string => {
    batchRef.current += 1;
    return 'grid-' + String(Date.now().toString(36)) + '-' + String(batchRef.current);
  }, []);

  const emit = useCallback((change: PendingChange, batchId: string | null) => {
    const handler = callbacksRef.current.onChange;
    if (!handler) return;
    seqRef.current += 1;
    handler({
      sheet: change.sheet,
      cell_ref: change.cell_ref,
      old_value: change.old_value,
      new_value: change.new_value,
      old_formula: change.old_formula,
      new_formula: change.new_formula,
      source: 'manual',
      reason: change.reason,
      proposed_at: new Date().toISOString(),
      seq: seqRef.current,
      batch_id: batchId,
    });
  }, []);

  /** Apply a block of entries in one go. Returns the changes actually made. */
  const applyEntries = useCallback(
    (sheetName: string, entries: Array<{ row: number; col: number; text: string }>): PendingChange[] => {
      const state = stateRef.current;
      const target = findSheet(state.sheets, sheetName);
      if (!target) return [];

      let sheetsNow = state.sheets;
      let ctxNow = state.ctx;
      const made: PendingChange[] = [];

      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        const ref = formatCellRef(entry.row, entry.col);
        const sheet = findSheet(sheetsNow, sheetName);
        if (!sheet) break;
        const before = cellAt(sheet, entry.row, entry.col);
        const oldValue = cellValueText(ctxNow, sheet.name, ref, before);
        const oldFormula = before && before.formula ? before.formula : null;
        const next = parseEntry(entry.text, before);
        const written = writeCell(sheetsNow, sheet.name, entry.row, entry.col, next, true);
        const built = buildContext(written.sheets);
        const newValue = cellValueText(built.ctx, sheet.name, ref, written.cell);
        const newFormula = written.cell.formula || null;
        if (newValue === oldValue && newFormula === oldFormula) continue;
        sheetsNow = written.sheets;
        ctxNow = built.ctx;
        made.push({
          sheet: sheet.name,
          cell_ref: ref,
          old_value: oldValue,
          new_value: newValue,
          old_formula: oldFormula,
          new_formula: newFormula,
          reason: null,
        });
      }

      if (made.length === 0) return [];
      historyRef.current.push({
        sheets: state.sheets,
        inverse: made.map((c) => invert(c, 'undo of change on ' + c.sheet + '!' + c.cell_ref)),
        structural: false,
      });
      const rebuilt = buildContext(sheetsNow);
      stateRef.current = { seeded: state.seeded, sheets: sheetsNow, ctx: rebuilt.ctx, error: rebuilt.error };
      setRev((r) => r + 1);
      return made;
    },
    [],
  );

  const commitEdit = useCallback(
    (sheetName: string, row: number, col: number, text: string) => {
      if (readOnly) return;
      const made = applyEntries(sheetName, [{ row: row, col: col, text: text }]);
      made.forEach((change) => emit(change, null));
    },
    [readOnly, applyEntries, emit],
  );

  /**
   * plan 8.1 "copy and paste of ranges": each pasted cell is one change event,
   * all of them batched under one batch id.
   */
  const pasteBlock = useCallback(
    (sheetName: string, row: number, col: number, block: string[][]) => {
      if (readOnly) return;
      const entries: Array<{ row: number; col: number; text: string }> = [];
      block.forEach((line, r) => {
        line.forEach((text, c) => {
          entries.push({ row: row + r, col: col + c, text: text });
        });
      });
      const made = applyEntries(sheetName, entries);
      if (made.length === 0) return;
      const batchId = nextBatchId();
      made.forEach((change) => emit(change, batchId));
    },
    [readOnly, applyEntries, emit, nextBatchId],
  );

  const runStructural = useCallback(
    (
      sheetName: string,
      apply: (sheets: WorkingSheet[]) => StructuralResult,
      headerAt: { row: number; col: number; text: string } | null,
      fire: (batchId: string, unshifted: UnshiftedFormula[]) => void,
    ) => {
      if (readOnly) return;
      const state = stateRef.current;
      const target = findSheet(state.sheets, sheetName);
      if (!target) return;

      let result: StructuralResult | null = null;
      try {
        result = apply(state.sheets);
      } catch (e) {
        // Loud, and contained: the grid paints its error panel instead of
        // tearing down the Workspace tab (G13).
        stateRef.current = Object.assign({}, state, {
          error: e instanceof Error ? e.message : String(e),
        });
        setRev((r) => r + 1);
        return;
      }
      if (result === null) return;

      const batchId = nextBatchId();
      const changes: PendingChange[] = result.shifts.map((shift) => {
        const oldSheet = findSheet(state.sheets, shift.sheet);
        const oldPos = parseCellRef(shift.old_ref);
        const oldCell = oldSheet && oldPos ? cellAt(oldSheet, oldPos.row, oldPos.col) : null;
        return {
          sheet: shift.sheet,
          cell_ref: shift.cell_ref,
          old_value: cellValueText(state.ctx, shift.sheet, shift.old_ref, oldCell),
          new_value: null,
          old_formula: shift.old_formula,
          new_formula: shift.new_formula,
          reason: 'reference shift after a structural insertion',
        };
      });

      let sheetsNow = result.sheets;
      if (headerAt) {
        const sheet = findSheet(sheetsNow, sheetName) as WorkingSheet;
        const before = cellAt(sheet, headerAt.row, headerAt.col);
        const next = parseEntry(headerAt.text, before);
        const written = writeCell(sheetsNow, sheetName, headerAt.row, headerAt.col, next, true);
        sheetsNow = written.sheets;
        changes.push({
          sheet: sheetName,
          cell_ref: formatCellRef(headerAt.row, headerAt.col),
          old_value: null,
          new_value: headerAt.text,
          old_formula: null,
          new_formula: null,
          reason: 'heading of the inserted column',
        });
      }

      const built = buildContext(sheetsNow);
      // Fill in the values the shifted formulas now produce, read from the
      // rebuilt context so a total that moved still reports what it shows.
      const withValues = changes.map((change) => {
        if (change.new_formula === null && change.old_formula === null) return change;
        const sheet = findSheet(sheetsNow, change.sheet);
        const pos = parseCellRef(change.cell_ref);
        const cell = sheet && pos ? cellAt(sheet, pos.row, pos.col) : null;
        return Object.assign({}, change, {
          new_value: cellValueText(built.ctx, change.sheet, change.cell_ref, cell),
        });
      });

      historyRef.current.push({
        sheets: state.sheets,
        inverse: withValues.map((c) => invert(c, 'undo of a structural insertion')),
        structural: true,
      });
      stateRef.current = { seeded: state.seeded, sheets: sheetsNow, ctx: built.ctx, error: built.error };
      setRev((r) => r + 1);

      fire(batchId, result.unshifted);
      withValues.forEach((change) => emit(change, batchId));
    },
    [readOnly, emit, nextBatchId],
  );

  const insertColumn = useCallback(
    (sheetName: string, atCol: number, count: number, header: string | null) => {
      const state = stateRef.current;
      const sheet = findSheet(state.sheets, sheetName);
      if (!sheet) return;
      const headerAt = header !== null && header !== ''
        ? { row: headerRowOf(sheet), col: atCol, text: header }
        : null;
      runStructural(
        sheetName,
        (list) => insertColumns(list, sheetName, atCol, count),
        headerAt,
        (batchId, unshifted) => {
          const handler = callbacksRef.current.onInsertColumn;
          if (!handler) return;
          handler({
            sheet: sheet.name,
            atCol: atCol,
            atColLetter: columnIndexToLetter(atCol),
            count: count,
            header: header,
            batch_id: batchId,
            unshifted: unshifted,
          });
        },
      );
    },
    [runStructural],
  );

  const insertRow = useCallback(
    (sheetName: string, atRow: number, count: number) => {
      const state = stateRef.current;
      const sheet = findSheet(state.sheets, sheetName);
      if (!sheet) return;
      runStructural(
        sheetName,
        (list) => insertRows(list, sheetName, atRow, count),
        null,
        (batchId, unshifted) => {
          const handler = callbacksRef.current.onInsertRow;
          if (!handler) return;
          handler({
            sheet: sheet.name,
            atRow: atRow,
            count: count,
            batch_id: batchId,
            unshifted: unshifted,
          });
        },
      );
    },
    [runStructural],
  );

  const undo = useCallback((): boolean => {
    if (readOnly) return false;
    const stack = historyRef.current;
    if (stack.length === 0) return false;
    const entry = stack[stack.length - 1];
    // A structural insertion is not reversed here: the props contract has no
    // "column removed" event, so undoing one in the grid alone would leave the
    // workspace record describing a workbook that no longer exists (G3).
    if (entry.structural) return false;
    stack.pop();
    const built = buildContext(entry.sheets);
    stateRef.current = { seeded: stateRef.current.seeded, sheets: entry.sheets, ctx: built.ctx, error: built.error };
    setRev((r) => r + 1);
    const batchId = entry.inverse.length > 1 ? nextBatchId() : null;
    entry.inverse.forEach((change) => emit(change, batchId));
    return true;
  }, [readOnly, emit, nextBatchId]);

  const getSheets = useCallback(() => toGridSheets(stateRef.current.sheets), []);

  const top = historyRef.current[historyRef.current.length - 1];
  return {
    sheets: stateRef.current.sheets,
    ctx: stateRef.current.ctx,
    error: stateRef.current.error,
    rev: rev,
    canUndo: !readOnly && historyRef.current.length > 0 && !(top && top.structural),
    commitEdit: commitEdit,
    pasteBlock: pasteBlock,
    insertColumn: insertColumn,
    insertRow: insertRow,
    undo: undo,
    getSheets: getSheets,
  };
}

